/**
 * Activity Tracker Utility
 * Tracks user activity events (mouse, keyboard, clicks, scroll, touch)
 * with throttling to avoid excessive callbacks
 */

/**
 * Creates an activity tracker that monitors user activity and calls a callback
 * @param {Function} onActivity - Callback function called when activity is detected
 * @param {number} throttleMs - Minimum time between activity callbacks (default: 5000ms = 5 seconds)
 * @returns {Object} Object with start() and stop() methods
 */
export const createActivityTracker = (onActivity, throttleMs = 5000) => {
  let lastActivityTime = 0;
  let isTracking = false;

  const handleActivity = () => {
    const now = Date.now();
    
    // Throttle: only trigger if enough time has passed since last callback
    if (now - lastActivityTime >= throttleMs) {
      lastActivityTime = now;
      if (onActivity && typeof onActivity === 'function') {
        onActivity();
      }
    }
  };

  const handleVisibilityChange = () => {
    if (!document.hidden) {
      handleActivity();
    }
  };

  const start = () => {
    if (isTracking) {
      return; // Already tracking
    }

    isTracking = true;
    lastActivityTime = Date.now();

    // Track various user activity events
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also track visibility change (when user switches back to tab)
    document.addEventListener('visibilitychange', handleVisibilityChange);
  };

  const stop = () => {
    if (!isTracking) {
      return; // Not tracking
    }

    isTracking = false;

    // Remove all event listeners
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.removeEventListener(event, handleActivity);
    });

    // Remove visibility change listener
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  return {
    start,
    stop,
    isTracking: () => isTracking
  };
};

export default createActivityTracker;
